import { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, Loader2, Bot, User, Settings2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useTaxStore } from '@/store/taxStore';
import { cn } from '@/lib/utils';

interface Action {
  tool: string;
  input: any;
  result: {
    success: boolean;
    message: string;
    data?: any;
  };
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: Action[];
}

export function AiChat() {
  const { selectedYear } = useTaxStore();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState<'anthropic' | 'openai'>('anthropic');
  const [includeContext, setIncludeContext] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Build conversation history from previous messages (exclude current one)
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const response = await api.chat({
        message: userMessage.content,
        provider,
        includeContext,
        year: selectedYear,
        conversationHistory,
      });

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.message || 'Unknown error'}. Please try again.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <>
      {/* Floating chat button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 p-4 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all z-40',
          isOpen && 'hidden'
        )}
        title="Ask AI"
      >
        <MessageSquare className="h-6 w-6" />
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] max-h-[80vh] bg-card border border-border rounded-xl shadow-2xl flex flex-col z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <span className="font-semibold">Tax Assistant</span>
              <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                {provider === 'anthropic' ? 'Claude' : 'GPT-4'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-1.5 rounded hover:bg-muted transition-colors"
                title="Settings"
              >
                <Settings2 className="h-4 w-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded hover:bg-muted transition-colors"
                title="Close"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Settings dropdown */}
          {showSettings && (
            <div className="p-3 border-b border-border bg-muted/30 space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">AI Provider</label>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setProvider('anthropic')}
                    className={cn(
                      'flex-1 py-1.5 px-3 text-sm rounded border transition-colors',
                      provider === 'anthropic'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border hover:bg-muted'
                    )}
                  >
                    Claude
                  </button>
                  <button
                    onClick={() => setProvider('openai')}
                    className={cn(
                      'flex-1 py-1.5 px-3 text-sm rounded border transition-colors',
                      provider === 'openai'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border hover:bg-muted'
                    )}
                  >
                    GPT-4
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">
                  Include tax context ({selectedYear})
                </label>
                <button
                  onClick={() => setIncludeContext(!includeContext)}
                  className={cn(
                    'w-10 h-5 rounded-full transition-colors relative',
                    includeContext ? 'bg-primary' : 'bg-muted-foreground/30'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                      includeContext ? 'left-5' : 'left-0.5'
                    )}
                  />
                </button>
              </div>
              <button
                onClick={clearChat}
                className="w-full py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded hover:bg-muted transition-colors"
              >
                Clear conversation
              </button>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                <Bot className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Your AI Tax Assistant</p>
                <p className="text-sm mt-1">
                  I can answer questions AND update your records!
                </p>
                <div className="mt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">Try saying:</p>
                  {[
                    'I spent $200 on office supplies',
                    'I made $5,000 from consulting',
                    'I drove 50 miles to meet a client',
                    'I donated $100 to charity',
                    'What deductions can I claim?',
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setInput(suggestion)}
                      className="block w-full text-left text-sm px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-3',
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.role === 'assistant' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className="max-w-[80%] space-y-2">
                  {/* Show action results */}
                  {message.actions && message.actions.length > 0 && (
                    <div className="space-y-1">
                      {message.actions.map((action, idx) => (
                        <div
                          key={idx}
                          className={cn(
                            'flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg',
                            action.result.success
                              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                              : 'bg-red-500/10 text-red-600 dark:text-red-400'
                          )}
                        >
                          {action.result.success ? (
                            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          <span>{action.result.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div
                    className={cn(
                      'rounded-lg px-3 py-2 text-sm',
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    )}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
                {message.role === 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="bg-muted rounded-lg px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-border">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a tax question..."
                rows={1}
                className="flex-1 resize-none px-3 py-2 bg-muted border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              AI responses are for informational purposes only. Consult a tax professional for advice.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
